import React, { useState } from "react";
import { Box, Card, CardContent, Typography, Grid, LinearProgress, CircularProgress, Tabs, Tab } from "@mui/material";
import { format, isSameDay } from "date-fns";
import WorkoutDetail from "./WorkoutDetail";
import EditWorkoutDialog from "./EditWorkoutDialog";
import SyncedHealthActivitiesSection from "./SyncedHealthActivitiesSection";
import { getWorkoutCalendarDate } from "../../../lib/firestoreDate";
import { useExercisesMap } from "../../plan/hooks/useExercisesMap";
import { formatNumber } from "../../../lib/formatNumber";
import { healthReportBelongsToCalendarDay, reportHasMeaningfulHealthData } from "../../../lib/healthUtils";
import { getActiveNutritionGoal, getkCal, getkCalFromMacros } from "../../../lib/nutritionUtils";
import NewColors from "../../../shared/theme/NewColors";

function workoutBelongsToCalendarDay(w, date) {
  if (!date) return false;
  const wd = getWorkoutCalendarDate(w);
  return Boolean(wd && isSameDay(wd, date));
}

function isPositiveNumber(value) {
  const n = Number(value);
  return typeof value !== "undefined" && value != null && !Number.isNaN(n) && n > 0;
}

function isVisceralFatDisplayable(value) {
  const v = Number(value);
  return Number.isFinite(v) && v > 0;
}

// Matches Activities tab DailyNutritionCard: one macro column (label, current, of goal, thin bar)
function MacroItem({ label, current, goal, color, unit = "g" }) {
  const cur = Number(current) || 0;
  const g = Number(goal) || 0;
  const pct = g > 0 ? Math.min(100, (cur / g) * 100) : 0;
  return (
    <Box sx={{ flex: 1, mx: 0.5, textAlign: "center" }}>
      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: "block", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight="bold" color="text.primary" sx={{ display: "block", mb: 0.25 }}>
        {formatNumber(cur)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
        {g > 0 ? `of ${formatNumber(g)}${unit}` : "—"}
      </Typography>
      {g > 0 && (
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 6,
            borderRadius: 1.5,
            backgroundColor: NewColors.sectionBackground,
            "& .MuiLinearProgress-bar": { borderRadius: 1.5, backgroundColor: color },
          }}
        />
      )}
    </Box>
  );
}

const TAB_WORKOUTS = 0;
const TAB_HEALTH = 1;

const DayReport = ({ date, workouts, healthReports, client, organizationId, clientId, weightUnit = "lbs", aggregated, loading = false }) => {
  const { findById } = useExercisesMap(organizationId, !!organizationId);
  const [tabValue, setTabValue] = useState(TAB_WORKOUTS);
  const [editorWorkout, setEditorWorkout] = useState(null);

  if (!date) return null;

  const dayWorkouts = (workouts ?? []).filter((w) => workoutBelongsToCalendarDay(w, date));
  const dayHealth = (healthReports ?? [])
    .filter((r) => healthReportBelongsToCalendarDay(r, date))
    .sort((a, b) => {
      const ta = (a.updatedOn instanceof Date ? a.updatedOn.getTime() : null) ??
        (a.createdOn instanceof Date ? a.createdOn.getTime() : null) ??
        0;
      const tb = (b.updatedOn instanceof Date ? b.updatedOn.getTime() : null) ??
        (b.createdOn instanceof Date ? b.createdOn.getTime() : null) ??
        0;
      return tb - ta;
    });
  const latestHealth = dayHealth[0];
  const rd = latestHealth?.reportData ?? {};
  // Goals for the day: stored at document top level (reportHealth doc has reportData + clientData)
  const reportClientData = latestHealth?.clientData ?? rd.clientData;
  const dateStr = format(date, "EEEE, MMMM d, yyyy");

  const hasWorkouts = dayWorkouts.length > 0;
  const hasHealth = latestHealth && reportHasMeaningfulHealthData(latestHealth);

  // Goals for this day: prefer goals stored in reportData.clientData (carbs, proteins, fats for that day)
  const goalFromReport =
    reportClientData &&
    (Number(reportClientData.carbs) > 0 ||
      Number(reportClientData.proteins) > 0 ||
      Number(reportClientData.fats) > 0)
      ? {
          carbs: Number(reportClientData.carbs) || 0,
          proteins: Number(reportClientData.proteins) || 0,
          fats: Number(reportClientData.fats) || 0,
          kCal: getkCal(reportClientData),
        }
      : null;
  const activeGoal = getActiveNutritionGoal(client ?? {});
  const goalGrams =
    goalFromReport ??
    (activeGoal
      ? {
          proteins: Number(activeGoal.proteins) || 0,
          carbs: Number(activeGoal.carbs) || 0,
          fats: Number(activeGoal.fats) || 0,
          kCal: getkCal(activeGoal),
        }
      : null);
  const goalName = reportClientData?.activeGoalName ?? activeGoal?.name ?? null;

  const hasNutritionInReport =
    rd.carbs != null || rd.proteins != null || rd.fats != null;

  const syncedActivities = Array.isArray(rd.activities) ? rd.activities : [];
  const hasSyncedActivities = syncedActivities.length > 0;
  const hasHealthTilesBesidesActivities = Boolean(
    (rd.weight != null && Number(rd.weight) > 0) ||
      (rd.steps != null && rd.steps > 0) ||
      isPositiveNumber(rd.averageHeartRate) ||
      isPositiveNumber(rd.fatPercentage) ||
      isPositiveNumber(rd.muscleMass) ||
      isVisceralFatDisplayable(rd.visceralFat) ||
      isPositiveNumber(rd.waist) ||
      isPositiveNumber(rd.hydrationPercentage) ||
      isPositiveNumber(rd.metabolicAge) ||
      hasNutritionInReport ||
      (rd.water != null && rd.water > 0)
  );

  const handleTabChange = (_, newValue) => setTabValue(newValue);

  return (
    <>
      <Card elevation={0} sx={{ mt: 2, overflow: "hidden" }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box
          sx={{
            mb: 2,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.5 }}>
            Selected day
          </Typography>
          <Typography variant="h6" fontWeight={700} color="primary" sx={{ mt: 0.5 }}>
            {dateStr}
          </Typography>
        </Box>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            mb: 2,
            minHeight: 48,
            "& .MuiTab-root": { minHeight: 48, fontWeight: 600, textTransform: "none" },
          }}
        >
          <Tab label="Training" id="day-report-tab-workouts" aria-controls="day-report-panel-workouts" />
          <Tab label="Health" id="day-report-tab-health" aria-controls="day-report-panel-health" />
        </Tabs>

        {tabValue === TAB_WORKOUTS && (
          <Box id="day-report-panel-workouts" role="tabpanel" aria-labelledby="day-report-tab-workouts">
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
                <CircularProgress size={24} />
                <Typography color="text.secondary">Loading…</Typography>
              </Box>
            ) : hasWorkouts ? (
              dayWorkouts.map((workout) => (
                <Box key={workout.id} sx={{ mb: 1.5 }}>
                  <WorkoutDetail
                    workout={workout}
                    findById={findById}
                    weightUnit={weightUnit}
                    aggregated={aggregated}
                    organizationId={organizationId}
                    clientId={clientId}
                    onEditWorkout={setEditorWorkout}
                  />
                </Box>
              ))
            ) : (
              <Box>
                <Typography color="text.secondary">No workout data for this day.</Typography>
                {hasSyncedActivities ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Outside-the-program fitness from Apple Health (runs, rides, walks, etc.) is on the
                    Health tab for this day.
                  </Typography>
                ) : null}
              </Box>
            )}
          </Box>
        )}

        {tabValue === TAB_HEALTH && (
          <Box id="day-report-panel-health" role="tabpanel" aria-labelledby="day-report-tab-health">
            {hasHealth ? (
          <Box
            sx={{
              p: 1.5,
              backgroundColor: NewColors.sectionBackground,
              borderRadius: 1,
            }}
          >
            <Grid container spacing={2}>
              {rd.weight != null && Number(rd.weight) > 0 && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Weight
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatNumber(rd.weight)} {weightUnit}
                  </Typography>
                </Grid>
              )}
              {rd.steps != null && rd.steps > 0 && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Steps
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatNumber(rd.steps)}
                  </Typography>
                </Grid>
              )}
              {isPositiveNumber(rd.averageHeartRate) && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Avg heart rate
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatNumber(rd.averageHeartRate)} bpm
                  </Typography>
                </Grid>
              )}
              {isPositiveNumber(rd.fatPercentage) && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Body fat
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatNumber(rd.fatPercentage)}%
                  </Typography>
                </Grid>
              )}
              {isPositiveNumber(rd.muscleMass) && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Muscle mass
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatNumber(rd.muscleMass)}%
                  </Typography>
                </Grid>
              )}
              {isVisceralFatDisplayable(rd.visceralFat) && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Visceral fat
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatNumber(rd.visceralFat)}
                  </Typography>
                </Grid>
              )}
              {isPositiveNumber(rd.waist) && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Waist
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatNumber(rd.waist)} in
                  </Typography>
                </Grid>
              )}
              {isPositiveNumber(rd.hydrationPercentage) && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Hydration
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatNumber(rd.hydrationPercentage)}%
                  </Typography>
                </Grid>
              )}
              {isPositiveNumber(rd.metabolicAge) && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Metabolic age
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatNumber(rd.metabolicAge)} years
                  </Typography>
                </Grid>
              )}
              {hasNutritionInReport ? (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Nutrition {goalName ? `(${goalName})` : ""}
                  </Typography>
                  {/* Calories summary (matches Activities tab DailyNutritionCard) */}
                  {(() => {
                    const currentKcal = getkCalFromMacros(rd);
                    const goalKcal = goalGrams?.kCal ?? 0;
                    const calPct = goalKcal > 0 ? (currentKcal / goalKcal) * 100 : 0;
                    return (
                      <Box
                        sx={{
                          backgroundColor: `${NewColors.nutrition.calories}1A`,
                          borderRadius: 1.5,
                          p: 2,
                          mb: 2,
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Box>
                            <Typography variant="body2" fontWeight={500} sx={{ color: NewColors.nutrition.calories }}>
                              Total Calories
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="text.primary">
                              {formatNumber(currentKcal)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {goalKcal > 0 ? `of ${formatNumber(goalKcal)} kcal` : "—"}
                            </Typography>
                          </Box>
                          {goalKcal > 0 && (
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: "50%",
                                backgroundColor: NewColors.cardBackground,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Typography
                                variant="body1"
                                fontWeight="bold"
                                sx={{
                                  color:
                                    calPct >= 100
                                      ? NewColors.status.success
                                      : NewColors.nutrition.calories,
                                }}
                              >
                                {Math.round(calPct)}%
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })()}
                  {/* Macros row: Carbs | Fats | Protein (matches Activities tab order and layout) */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                    <MacroItem
                      label="Carbs"
                      current={rd.carbs ?? 0}
                      goal={goalGrams?.carbs}
                      color={NewColors.nutrition.carbs}
                      unit="g"
                    />
                    <MacroItem
                      label="Fats"
                      current={rd.fats ?? 0}
                      goal={goalGrams?.fats}
                      color={NewColors.nutrition.fats}
                      unit="g"
                    />
                    <MacroItem
                      label="Protein"
                      current={rd.proteins ?? 0}
                      goal={goalGrams?.proteins}
                      color={NewColors.nutrition.proteins}
                      unit="g"
                    />
                  </Box>
                </Grid>
              ) : null}
              {rd.water != null && rd.water > 0 && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Water
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatNumber(rd.water)}
                  </Typography>
                </Grid>
              )}
            </Grid>
            {hasSyncedActivities ? (
              <SyncedHealthActivitiesSection
                activities={syncedActivities}
                showDivider={hasHealthTilesBesidesActivities}
              />
            ) : null}
          </Box>
            ) : loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
                <CircularProgress size={24} />
                <Typography color="text.secondary">Loading…</Typography>
              </Box>
            ) : (
              <Typography color="text.secondary">No health data for this day.</Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
      <EditWorkoutDialog
        open={Boolean(editorWorkout)}
        workout={editorWorkout}
        organizationId={organizationId}
        clientId={clientId}
        onClose={() => setEditorWorkout(null)}
      />
    </>
  );
};

export default DayReport;
